<template>
  <v-layout>
    <v-dialog v-model="submitDialog" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">Link expenses to a shift</v-card-title>
        <v-card-text>
          <v-select label="Link to shift" :items="shiftArray" v-model="linkedShift" item-text="text" item-value="key"
            no-data-text="No full shifts to link to">
          </v-select>
          Submitting this claim will link the claim to the shift above.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="accent" flat @click.native="submitDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" :loading="isLoading" flat @click.native="submitClaim">Submit Claim</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="errorDialog" persistent>
      <v-card>
        <v-card-title class="headline">Offline</v-card-title>
        <v-card-text>
          <v-alert v-model="errorDialog" icon="cloud_off" type="error">You are currently offline, Please try again
            later</v-alert>
          <p>{{ errorText }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" flat to="/">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="unauthorisedDialog" persistent>
      <v-card>
        <v-card-title class="headline">Access Denied</v-card-title>
        <v-card-text>
          <v-alert v-model="unauthorisedDialog" icon="block" type="error">The server could not authorise this request.
            Please try logging in again and submitting the claim.</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="logout()">Exit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="currencyDialog" max-width="1200" persistent>
      <v-card>
        <v-card-title class="headline">Change Expenses Currency</v-card-title>
        <v-container fluid grid-list-xs align-content-center>
          <v-layout row wrap>
            <v-flex xs6 sm2 lg1 v-if="currency.show == 1" v-for="(currency, key, index) in currencies" :key="index"
              class="text-xs-center">
              <div @click="setCurrency(key)">
                <v-btn fab large class="green white--text hidden-sm-and-down">
                  <span style="font-size:28px;">{{ currency.text }}</span>
                </v-btn>
                <v-btn fab class="green white--text display hidden-xs-only hidden-md-and-up">
                  <span style="font-size:22px;">{{ currency.text }}</span>
                </v-btn>
                <v-btn fab small class="green white--text hidden-sm-and-up">
                  <span style="font-size:16px;">{{ currency.text }}</span>
                </v-btn>
              </div>
              <p>{{ currency.desc }}</p>
            </v-flex>
            <v-flex xs12>
              <v-card-text>Changing currency will affect all claim items. If you need to make claims with different
                currencies, submit the claim with the first currency and then change the currency and make a second
                claim.</v-card-text>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card>
    </v-dialog>

    <v-flex xs12 md6 offset-md3>
      <v-card>
        <v-container fluid>
          <div class="mb-3">
            <v-btn block class="primary" v-if="hasClaim" @click.stop="submitDialog = true" :loading="isLoading">Submit
              Claim</v-btn>
          </div>
          <v-fab-transition>
            <v-btn absolute dark fab bottom right color="pink" v-if="!claimContainsMileage" @click="addClaim"
              v-show="newClaim">
              <v-icon>add</v-icon>
            </v-btn>
          </v-fab-transition>
          <v-fab-transition>
            <v-btn fab dark right small absolute v-if="!claimContainsMileage" color="green"
              @click="() => currencyDialog = true">
              {{ currentCurrency.text }}
            </v-btn>
          </v-fab-transition>
          <v-flex xs11>
            <v-alert type="info" :value="!hasClaim" class="mb-5">
              To start a claim add an item using the + button.
            </v-alert>
          </v-flex>
          <v-list subheader v-if="hasClaim">
            <v-subheader>
              Current Claim
            </v-subheader>
            <v-list-tile avatar v-for="(claim, i) in currentClaim" v-bind:key="i">
              <v-list-tile-content>
                <v-list-tile-title v-if="claim.type !== 'Other'">{{ claim.type }}</v-list-tile-title>
                <v-list-tile-title v-if="claim.type === 'Other'">{{ claim.desc }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-avatar>
                <strong><span v-if="currentCurrency.text != 'M'">{{ currentCurrency.text }}</span>{{ claim.cost |
                  currency}} <span v-if="currentCurrency.text == 'M'">{{ currentCurrency.currency }}</span></strong>
              </v-list-tile-avatar>
            </v-list-tile>
            <v-divider inset></v-divider>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title><strong>Total</strong></v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-avatar>
                <strong><span v-if="currentCurrency.text != 'M'">{{ currentCurrency.text }}</span>{{ total | currency }}
                  <span v-if="currentCurrency.text == 'M'">{{ currentCurrency.currency }}</span></strong>
              </v-list-tile-avatar>
            </v-list-tile>
          </v-list>
        </v-container>
      </v-card>
    </v-flex>
    <v-snackbar v-model="snackbar" :timeout="snackbarTimeout">
      {{ snackbarText }}
      <v-btn dark flat @click.native="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-dialog v-model="detailsDialog" fullscreen transition="dialog-bottom-transition" :overlay="false">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Add Item</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark flat @click.native="detailsDialog = false">Cancel</v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-stepper vertical v-model="detailsStepper" v-if="detailsDialog" light>
          <v-stepper-step step="1" :complete="detailsStepper > 1" editable>Add details</v-stepper-step>
          <v-stepper-content step="1">
            <v-form v-model="detailsValid" ref="form" lazy-validation>
              <v-select v-if="!hideExpenseDropdown" :items="computedExpenseTypes" item-text="expense_name"
                item-value="expense_name" v-model="currentItem.type" label="Expense Type" :rules="typeRules" required
                single-line :menu-props="{ maxHeight: 500 }" @input="checkDescription()"></v-select>
              <v-text-field label="Description" v-model="currentItem.desc"
                v-if="currentItem.type == 'Other' || (company === 'lfp' || company === 'aquahaulage' || company === 'bmtransport')"
                required :rules="descRules" :counter="50" @keyup.enter="gotoStep2"></v-text-field>
              <v-text-field v-if="currentItem.type === 'Parking' && (taSettings.parking_location_provider.value == '1' || taSettings.parking_location_provider.value == '2')" :disabled="canEditExpense == false" 
              label="Provider" v-model="currentItem.provider" :rules="providerRules" :counter="50"
                @keyup.enter="gotoStep2"></v-text-field>
                <v-text-field v-if="currentItem.type === 'Parking' 
                && (taSettings.parking_location_provider.value == '1' || taSettings.parking_location_provider.value == '2')" 
                :disabled="canEditExpense == false"
                label="Location" v-model="currentItem.location" 
                :rules="locationsRules" :counter="50"
                @keyup.enter="gotoStep2"></v-text-field>
              <v-text-field :disabled="canEditExpense == false" :label="currentCurrency.text + ' Amount'" v-model="currentItem.cost" :rules="currencyRules"
                type="number" @keyup.enter="gotoStep2"></v-text-field>
              <v-btn class="primary" block @click.native.stop="gotoStep2" :disabled="isLoading">Add Details</v-btn>
            </v-form>
          </v-stepper-content>
          <v-stepper-step step="2" :complete="detailsStepper > 2" editable>Take photo</v-stepper-step>
          <v-stepper-content step="2">
            <v-btn block class="primary" @click="triggerPick">
              <v-icon left dark>photo_camera</v-icon>
              Add Photo
            </v-btn>
            <input id="imagePicker" type="file" accept="image/jpeg,android/camera-workaround" @change="saveImages" style="display:none;">
            <canvas id="imageCanvas" hidden></canvas>
          </v-stepper-content>
          <v-stepper-step step="3" :complete="detailsStepper > 3">Confirm and Save</v-stepper-step>
          <v-stepper-content step="3">
            <v-btn class="primary" :loading="isLoading" block @click.native.stop="addCurrentItem">Add to Claim</v-btn>
            <h3>Expense Type: {{ currentItem.type }}</h3>
            <h3 v-if="currentItem.desc">Description: {{ currentItem.desc }}</h3>
            <h3 v-if="currentCurrency.text != 'M'">Cost: {{ currentCurrency.text }}{{ currentItem.cost | currency }}</h3>
            <h3 v-if="currentCurrency.text == 'M'">{{ currentItem.cost | currency }} {{ currentCurrency.currency }}</h3>
            <img style="width:100%;" :src="currentItem.photo" />
          </v-stepper-content>
        </v-stepper>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import dbPromise from '../dbPromise'
import { storage } from '../firebase'
import axios from 'axios'
import moment from 'moment'
import '../assets/css/materialdesignicons.min.css'
export default {
  props: ['shiftID'],
  data () {
    return {
      currentCurrency: this.$store.getters.currency,
      url: this.$store.getters.url,
      isLoading: false,
      emp: this.$store.getters.user.empID,
      company: this.$store.getters.company,
      errorDialog: false,
      errorText: '',
      unauthorisedDialog: false,
      sending: 0,
      snackbar: false,
      submitDialog: false,
      snackbarText: '',
      currentJob: false,
      snackbarTimeout: 1000,
      newClaim: true,
      currentClaim: [],
      linkedShift: null,
      hideExpenseDropdown: false,
      currentItem: {
        cost: '',
        desc: '',
        type: '',
        photo: ''
      },
      expenseTypes: [],
      detailsDialog: false,
      detailsStepper: 1,
      detailsValid: false,
      locationsRules: [
        v => (this.taSettings && this.taSettings.parking_location_provider && (this.taSettings.parking_location_provider.value === '2'))
          ? (!!v || 'Location is required')
          : true,
        v => !v || v.length <= 50 || 'Location must be less than 50 characters'
      ],
      providerRules: [
        v => (this.taSettings && this.taSettings.parking_location_provider && (this.taSettings.parking_location_provider.value === '2'))
          ? (!!v || 'Provider is required')
          : true,
        v => !v || v.length <= 50 || 'Provider must be less than 50 characters'
      ],
      descRules: [
        v => !!v || 'Description is required',
        v => (v && v.length <= 50) || 'Description must be less than 50 characters'
      ],
      currencyRules: [
        v => !!v || 'Cost is required'
      ],
      typeRules: [
        v => !!v || 'Expense Type is required'
      ],
      currencyDialog: false,
      currencies: {
        gbp: {
          desc: 'British Pound',
          text: '£',
          show: true
        },
        eur: {
          desc: 'Euro',
          text: '€',
          show: true
        }
        // miles: {
        //   desc: 'Miles',
        //   text: 'M',
        //   show: true
        // }
        // pln: {
        //   desc: 'Polish Zloty',
        //   text: 'zł',
        //   show: true
        // },
        // all: {
        //   desc: 'Albanian Lek',
        //   text: 'L',
        //   show: true
        // },
        // azn: {
        //   desc: 'Azerbaijani manat',
        //   text: '₼'
        //  show: true
        // },
        // byn: {
        //   desc: 'Belarusian ruble',
        //   text: 'Br',
        //   show: true
        // },
      },
      canEditExpense: true
    }
  },
  created () {
    dbPromise().then((db) => {
      let tx = db.transaction('current-expenses')
      let currentExpenses = tx.objectStore('current-expenses')
      return (currentExpenses.get(this.emp))
    }).then((val) => {
      if (val !== undefined) {
        this.currentClaim = val
      }
    })
    this.linkedShift = this.shiftID !== undefined ? this.shiftID : null

    this.getExpenseTypes()

    if (this.company.toLowerCase() === 'lfp' || this.company.toLowerCase() === 'aquahaulage') {
      // this.expenseTypes.push('Mileage')
      this.currencies.miles = {
        desc: 'Miles',
        text: 'M',
        show: false
      }
    }

    if (this.currentCurrency.text === null || this.currentCurrency.text === undefined || this.currentCurrency.currency === null || this.currentCurrency.currency === undefined) {
      this.currentCurrency = {
        currency: 'gbp',
        text: '£'
      }
    }
  },
  mounted () {
    this.$store.dispatch('checkLoginTime')
  },
  computed: {
    taSettings () {
      return this.$store.getters.taSettings
    },
    computedExpenseTypes () {
      let output = []
      for (let expense in this.expenseTypes) {
        let currentExpense = this.expenseTypes[expense]
        if (currentExpense.expense_name === 'Mileage' && !this.currentClaim.some(claim => claim.type === 'Mileage') && this.currentClaim.length > 0) { // LFP LOGIC - Mileage cannot be combined with other expense types, so if there is already an existing expense, hide the mileage option
          currentExpense.hidden = 1
        }
        if (currentExpense.hidden === 0) {
          output.push(currentExpense)
        }
      }
      return output
    },
    total () {
      let total = 0
      for (let claim in this.currentClaim) {
        total += (this.currentClaim[claim].cost * 1)
      }
      return total
    },
    hasClaim () {
      return this.currentClaim.length > 0
    },
    shiftArray () {
      let shiftArray = []
      this.$store.getters.timesheet.forEach(shift => {
        // if (shift.end !== undefined) {
        let text = moment(shift.start).format('DD/MM/YYYY HH:mm') + ' - ' + moment(shift.end).format('DD/MM/YYYY HH:mm')
        let value = shift.id
        shiftArray.push({ key: value, text: text })
        // }
      })
      shiftArray = shiftArray.reverse()
      return shiftArray
    },
    claimContainsMileage () {
      var containsMileage = this.currentClaim.some(claim => {
        return claim.type === 'Mileage'
      })
      if (containsMileage) {
        this.setCurrency('miles')
      }
      return containsMileage
    }
  },
  filters: {
    currency: function (value) {
      if (!value) return '0.00'
      value = value.toString()
      return parseFloat(value).toFixed(2)
    }
  },
  methods: {
    getExpenseTypes () {
      this.isLoading = true
      axios.post(this.url + `/tanda-service/getExpenseTypes.ajax.php`, { company: this.company })
        .then(result => {
          this.isLoading = false
          this.expenseTypes = result.data
        })
        .catch(e => {
          console.log(e)
        })
    },
    addClaim () {
      this.detailsDialog = true
      this.currentItem.type = ''
    },
    setCurrency (chosenCurrency) {
      this.currentCurrency.currency = chosenCurrency
      this.currentCurrency.text = this.currencies[chosenCurrency]['text']
      this.$store.dispatch('setCurrencyData', this.currentCurrency)
      this.currencyDialog = false
    },
    checkDescription () {
      if (this.currentItem.type !== 'Other') {
        this.currentItem.desc = ''
      }
      if (this.currentItem.type === 'Mileage') {
        this.setCurrency('miles')
      }

      // Find the matching expense type in computedExpenseTypes
      const matchedType = this.computedExpenseTypes.find(
        expense => expense.expense_name === this.currentItem.type
      )
      console.log(matchedType.can_edit)
      if (matchedType && matchedType.default_value !== undefined) {
        this.currentItem.cost = matchedType.default_value
      } else {
        this.currentItem.cost = ''
      }

      if (matchedType && (matchedType.can_edit === 1 || matchedType.can_edit === null)) {
        this.canEditExpense = true
      } else {
        this.canEditExpense = false
      }
    },
    gotoStep2 () {
      if (this.$refs.form.validate()) {
        console.log(this.currentItem.type)
        let nonImageExpenses = ['Mileage', 'Nightout (Free)', 'Nightout (International)', 'Night Out Allownace', 'Meal Allowance A', 'Meal Allownace B', 'Overtime Meal Allowance']
        if (!nonImageExpenses.includes(this.currentItem.type)) {
          this.detailsStepper = 2
        } else {
          this.detailsStepper = 3
        }
      }
    },
    triggerPick () {
      document.getElementById('imagePicker').click()
    },
    saveImages (e) {
      let file = e.target.files[0]
      // let filename = Date.now() + '.jpg'
      let reader = new FileReader()
      if (file) {
        reader.readAsArrayBuffer(file)
        reader.onload = (event) => {
          let exif = this.getExifData(event.target.result)
          let c = document.getElementById('imageCanvas')
          let ctx = c.getContext('2d')
          let img = new Image()
          img.src = URL.createObjectURL(e.target.files[0])
          img.onload = () => {
            if (exif > 4) {
              c.width = img.height
              c.height = img.width
              ctx.clearRect(0, 0, c.width, c.height)
              ctx.translate(img.height / 2, img.width / 2)
              if (exif > 6) {
                ctx.rotate(270 * Math.PI / 180)
              } else {
                ctx.rotate(90 * Math.PI / 180)
              }
              ctx.drawImage(img, -img.width / 2, -img.height / 2)
            } else {
              c.width = img.width
              c.height = img.height
              if (exif === 3 || exif === 4) {
                ctx.clearRect(0, 0, c.width, c.height)
                ctx.translate(img.width, img.height)
                ctx.rotate(180 * Math.PI / 180)
              }
              ctx.drawImage(img, 0, 0)
            }
            let cameraQuality = localStorage.getItem('cameraQuality') !== null && localStorage.getItem('cameraQuality') !== undefined ? localStorage.cameraQuality : 50
            cameraQuality = cameraQuality / 100
            let base64data = c.toDataURL('image/jpeg', cameraQuality)
            this.currentItem.photo = base64data
            this.detailsStepper = 3
          }
        }
      }
    },
    addCurrentItem () {
      this.isLoading = true
      let filename = Date.now() + '.jpg'
      dbPromise().then((db) => {
        let tx = db.transaction('image-store', 'readwrite')
        let imageStore = tx.objectStore('image-store')
        imageStore.put(this.currentItem.photo, filename)
        return tx.complete
      }).then(() => {
        console.log('data saved locally')
        this.isLoading = false
        this.currentClaim.push({
          cost: this.currentItem.cost,
          desc: this.currentItem.desc,
          type: this.currentItem.type,
          ...(this.currentItem.type === 'Parking' ? {
            location: this.currentItem.location,
            provider: this.currentItem.provider
          } : {}),
          photo: filename
        })
        dbPromise().then((db) => {
          let tx = db.transaction('current-expenses', 'readwrite')
          let currentExpenses = tx.objectStore('current-expenses')
          currentExpenses.put(this.currentClaim, this.emp)
          return tx.complete
        }).then(() => {
          console.log('current expense saved')
        })

        this.currentItem = {
          cost: '',
          desc: '',
          type: '',
          photo: '',
          location: '',
          provider: '',
          currency: ''
        }
        this.detailsStepper = 1
        this.detailsDialog = false
      })
    },
    getExifData (image) {
      let view = new DataView(image)
      if (view.getUint16(0, false) !== 0xFFD8) return -2
      let length = view.byteLength
      let offset = 2
      while (offset < length) {
        let marker = view.getUint16(offset, false)
        offset += 2
        if (marker === 0xFFE1) {
          if (view.getUint32(offset += 2, false) !== 0x45786966) return -1
          let little = view.getUint16(offset += 6, false) === 0x4949
          offset += view.getUint32(offset + 4, little)
          let tags = view.getUint16(offset, little)
          offset += 2
          for (let i = 0; i < tags; i++) {
            if (view.getUint16(offset + (i * 12), little) === 0x0112) {
              return view.getUint16(offset + (i * 12) + 8, little)
            }
          }
        } else if ((marker & 0xFF00) !== 0xFF00) {
          break
        } else {
          offset += view.getUint16(offset, false)
        }
      }
      return -1
    },
    submitClaim () {
      this.isLoading = true
      let imagesToDelete = []
      for (let item in this.currentClaim) {
        dbPromise().then((db) => {
          let tx = db.transaction('image-store')
          let imageStore = tx.objectStore('image-store')
          return (imageStore.get(this.currentClaim[item].photo))
        }).then((val) => {
          imagesToDelete.push(this.currentClaim[item].photo)
          if (!val) {
            val = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAMCAgICAgMCAgIDAwMDBAYEBAQEBAgGBgUGCQgKCgkICQkKDA8MCgsOCwkJDRENDg8QEBEQCgwSExIQEw8QEBD/2wBDAQMDAwQDBAgEBAgQCwkLEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBD/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAn/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFAEBAAAAAAAAAAAAAAAAAAAAAP/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AJVAA//Z'
            this.currentClaim[item].photo = 'no-image.jpg'
          }
          let imageData = val
          let ref = storage.ref(this.company + '/' + this.emp + '/' + this.currentClaim[item].photo)
          this.sending++
          ref.putString(imageData, 'data_url')
            .then((snap) => {
              return snap.ref.getDownloadURL()
            })
            .then(downloadURL => {
              this.sending--
              if (this.currentClaim[item].photo === 'no-image.jpg') {
                this.currentClaim[item].photo = ''
              } else {
                this.currentClaim[item].photo = downloadURL
              }
              if (this.sending === 0) {
                let shiftStartTime = -1
                if (this.linkedShift) {
                  const shiftIndex = this.shiftArray.findIndex(shift => {
                    return shift.key === this.linkedShift
                  })
                  let shiftArr = this.shiftArray[shiftIndex]['text'].split(' - ')
                  shiftStartTime = moment(shiftArr[0], 'DD/MM/YYYY HH:mm').format('YYYY-MM-DD HH:mm:ss')
                }
                axios.post(this.url + `/tanda-service/saveExpenses.ajax.php`, {
                  company: this.company,
                  emp: this.emp,
                  data: this.currentClaim,
                  shiftID: this.linkedShift,
                  currency: this.currentCurrency.currency,
                  shiftStartTime: shiftStartTime
                })
                  .then(response => {
                    dbPromise().then((db) => {
                      let tx = db.transaction('current-expenses', 'readwrite')
                      let currentExpenses = tx.objectStore('current-expenses')
                      return (currentExpenses.delete(this.emp))
                    }).then((val) => {
                      imagesToDelete.forEach((image) => {
                        dbPromise().then((db) => {
                          let tx = db.transaction('image-store', 'readwrite')
                          let imageStore = tx.objectStore('image-store')
                          return (imageStore.delete(image))
                        })
                      })
                      this.$router.push('/')
                    })
                  }).catch((error) => {
                    this.errorDialog = true
                    this.submitDialog = false
                    this.isLoading = false
                    this.errorText = error.message
                  })
              }
            })
            .catch((error) => {
              if (error.code === 'storage/unauthorized') {
                this.unauthorisedDialog = true
                this.submitDialog = false
                this.isLoading = false
              } else {
                this.errorText = error.message
                this.errorDialog = true
                this.submitDialog = false
                this.isLoading = false
              }
            })
        })
          .catch((error) => {
            console.log(error)
            this.isLoading = false
          })
      }
    },
    logout () {
      this.$store.dispatch('logout')
      this.$router.push('/SignIn')
    }
  },
  watch: {
    'currentItem.type': function (value, prevValue) {
      if (value !== 'Mileage' && this.currentCurrency.currency === 'miles' && value.length > 0) {
        this.currencyDialog = true
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Expenses.vue